import axios, { AxiosResponse, AxiosInstance } from 'axios';
import {
  LoginResponse,
  Material,
  Sablona,
  SberatelskiObjekt,
  LoginForm,
  ChangePasswordForm,
  TypPolozky,
  Objednavka,
  ObjednavkaDetail,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Helper function to generate full URL for multimedia files
export const getMediaUrl = (relativePath: string): string => {
  if (!relativePath) return '';
  // If relativePath already includes the domain, return as is
  if (relativePath.startsWith('http')) return relativePath;
  // If it's just a filename (without /uploads/), prepend /uploads/
  if (!relativePath.startsWith('/')) {
    return `${API_BASE_URL}/uploads/${relativePath}`;
  }
  // Otherwise, prepend the API base URL
  return `${API_BASE_URL}${relativePath}`;
};

// Helper function to generate thumbnail URL from filename
export const getThumbnailUrl = (filename: string): string => {
  if (!filename) return '';
  
  // If it starts with /, extract just the filename
  const cleanFilename = filename.startsWith('/uploads/') 
    ? filename.replace('/uploads/', '') 
    : filename.replace('/', '');
  
  // Replace extension with .jpg for videos and add thumb_ prefix
  const nameWithoutExt = cleanFilename.substring(0, cleanFilename.lastIndexOf('.'));
  const thumbnailFilename = `thumb_${nameWithoutExt}.jpg`;
  
  return `${API_BASE_URL}/uploads/${thumbnailFilename}`;
};

class ApiClient {
  private baseURL: string;
  private token: string | null = null;
  private axiosInstance: AxiosInstance;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.token = localStorage.getItem('token');
    
    // Create axios instance with baseURL
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    // Add token to headers if available
    if (this.token) {
      this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
    }

    // Response interceptor to handle token expiration
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('token', token);
    this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  removeToken() {
    this.token = null;
    localStorage.removeItem('token');
    delete this.axiosInstance.defaults.headers.common['Authorization'];
  }

  logout() {
    this.removeToken();
  }

  // Admin endpoints
  async login(credentials: LoginForm): Promise<LoginResponse> {
    const response: AxiosResponse<LoginResponse> = await this.axiosInstance.post('/admin/login', credentials);
    return response.data;
  }

  async changePassword(adminId: number, data: ChangePasswordForm): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.axiosInstance.put(`/admin/${adminId}/password`, data);
    return response.data;
  }

  // Material endpoints
  async getMaterials(): Promise<Material[]> {
    const response: AxiosResponse<Material[]> = await this.axiosInstance.get('/materialy');
    return response.data;
  }

  async getMaterialWithAllLanguages(id: number): Promise<{ id: number; nazev: any; popis: any }> {
    const response: AxiosResponse<{ id: number; nazev: any; popis: any }> = await this.axiosInstance.get(`/materialy/${id}/all-languages`);
    return response.data;
  }

  async createMaterial(data: { nazev: any; popis: any }): Promise<{ message: string; material: Material }> {
    const response: AxiosResponse<{ message: string; material: Material }> = await this.axiosInstance.post('/materialy', data);
    return response.data;
  }

  async updateMaterial(id: number, data: { nazev: any; popis: any }): Promise<{ message: string; material: Material }> {
    const response: AxiosResponse<{ message: string; material: Material }> = await this.axiosInstance.put(`/materialy/${id}`, data);
    return response.data;
  }

  async deleteMaterial(id: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.axiosInstance.delete(`/materialy/${id}`);
    return response.data;
  }

  // Type endpoints
  async getTypyPolozek(sekce?: number): Promise<TypPolozky[]> {
    const params = sekce !== undefined ? `?sekce=${sekce}&language=cz` : '?language=cz';
    const response = await this.axiosInstance.get(`/typ-polozky${params}`);
    return response.data;
  }

  async getTypPolozkyById(id: number): Promise<TypPolozky> {
    const response = await this.axiosInstance.get(`/typ-polozky/${id}?language=cz`);
    return response.data;
  }

  // SberatelskiObjekt endpoints
  async getObjekty(page: number = 1, limit: number = 10, search?: string, sekce?: number): Promise<{ objektIds: number[]; total: number; page: number; totalPages: number }> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    params.append('includeHidden', 'true'); // Admin should see all objects including hidden ones
    if (search) {
      params.append('search', search);
    }
    if (sekce !== undefined) {
      params.append('sekce', sekce.toString());
    }
    
    const response = await this.axiosInstance.get(`/sberatelske-objekty?${params.toString()}`);
    return response.data;
  }

  async getObjektyByIds(ids: number[]): Promise<{ objekty: SberatelskiObjekt[]; count: number }> {
    if (ids.length === 0) {
      return { objekty: [], count: 0 };
    }
    const response = await this.axiosInstance.get(`/sberatelske-objekty/seznam/ids?ids=${ids.join(',')}&language=cz&includeHidden=true`);
    return response.data;
  }

  async deleteObjekt(id: number): Promise<{ message: string }> {
    const response = await this.axiosInstance.delete(`/sberatelske-objekty/${id}`);
    return response.data;
  }

  async toggleSkryta(id: number): Promise<{ success: boolean; skryta: boolean; message: string }> {
    const response = await this.axiosInstance.post(`/sberatelske-objekty/${id}/toggle-skryta`);
    return response.data;
  }

  async getObjektDetail(id: number): Promise<any> {
    const response = await this.axiosInstance.get(`/sberatelske-objekty/${id}/all-languages`);
    return response.data;
  }

  async createObjekt(data: {
    sklad_id: string;
    nazev_cz: string;
    nazev_en: string;
    nazev_de: string;
    popis_cz: string;
    popis_en: string;
    popis_de: string;
    cena?: number;
    rozmery?: string;
    vaha?: string;
    rezervovatelne: boolean;
    skryta: boolean;
    pocet_ks_sklad?: number;
    typ_polozky_id: number;
    material_ids: number[];
    sekce: number;
  }): Promise<{ message: string; objekt: SberatelskiObjekt }> {
    const response = await this.axiosInstance.post(`/sberatelske-objekty/multilang?sekce=${data.sekce}`, {
      sklad_id: data.sklad_id,
      nazev: {
        cz: data.nazev_cz,
        en: data.nazev_en,
        de: data.nazev_de
      },
      popis: {
        cz: data.popis_cz,
        en: data.popis_en,
        de: data.popis_de
      },
      cena: data.cena,
      rozmery: data.rozmery,
      vaha: data.vaha,
      rezervovatelne: data.rezervovatelne,
      skryta: data.skryta,
      pocet_ks_sklad: data.pocet_ks_sklad ?? 1,
      typ_polozky_id: data.typ_polozky_id,
      material_ids: data.material_ids
    });
    return response.data;
  }

  async updateObjekt(id: number, data: {
    sklad_id?: string;
    nazev_cz?: string;
    nazev_en?: string;
    nazev_de?: string;
    popis_cz?: string;
    popis_en?: string;
    popis_de?: string;
    cena?: number;
    rozmery?: string;
    vaha?: string;
    rezervovatelne?: boolean;
    skryta?: boolean;
    pocet_ks_sklad?: number;
    typ_polozky_id?: number;
    material_ids?: number[];
  }): Promise<{ message: string; objekt: SberatelskiObjekt }> {
    const response = await this.axiosInstance.put(`/sberatelske-objekty/${id}/multilang`, {
      sklad_id: data.sklad_id,
      nazev: {
        cz: data.nazev_cz,
        en: data.nazev_en,
        de: data.nazev_de
      },
      popis: {
        cz: data.popis_cz,
        en: data.popis_en,
        de: data.popis_de
      },
      cena: data.cena,
      rozmery: data.rozmery,
      vaha: data.vaha,
      rezervovatelne: data.rezervovatelne,
      skryta: data.skryta,
      pocet_ks_sklad: data.pocet_ks_sklad,
      typ_polozky_id: data.typ_polozky_id,
      material_ids: data.material_ids
    });
    return response.data;
  }

  async uploadMultimedia(objektId: number, file: File, poradi?: number): Promise<{ message: string; multimedia: any }> {
    const formData = new FormData();
    formData.append('file', file);
    if (poradi !== undefined) {
      formData.append('poradi', poradi.toString());
    }

    const response = await this.axiosInstance.post(`/sberatelske-objekty/${objektId}/multimedia`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  async deleteMultimedia(multimediaId: number): Promise<{ message: string }> {
    const response = await this.axiosInstance.delete(`/sberatelske-objekty/multimedia/${multimediaId}`);
    return response.data;
  }

  // Sablony (templates) endpoints – admin only
  async getSablony(language: string = 'cz'): Promise<Sablona[]> {
    const response: AxiosResponse<Sablona[]> = await this.axiosInstance.get(`/sablony?language=${language}`);
    return response.data;
  }

  async getSablonaById(id: number, language: string = 'cz'): Promise<Sablona> {
    const response: AxiosResponse<Sablona> = await this.axiosInstance.get(`/sablony/${id}?language=${language}`);
    return response.data;
  }

  // Objednavky endpoints (admin only)
  async getObjednavky(): Promise<Objednavka[]> {
    const response: AxiosResponse<Objednavka[]> = await this.axiosInstance.get('/objednavky');
    return response.data;
  }

  async getObjednavkaById(id: number): Promise<ObjednavkaDetail> {
    const response: AxiosResponse<ObjednavkaDetail> = await this.axiosInstance.get(`/objednavky/${id}`);
    return response.data;
  }

  async updateObjednavka(
    id: number,
    data: Partial<Pick<Objednavka, 'jmeno' | 'email' | 'telefon' | 'adresa' | 'poznamka' | 'stav'>>
  ): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> =
      await this.axiosInstance.put(`/objednavky/${id}`, data);
    return response.data;
  }

}

export const apiClient = new ApiClient(API_BASE_URL);
export default apiClient;
